import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { TOKEN_KEY } from '@/api/index';
import meKeys from '@/api/queries/me/keyFactory';
import useGetChatAgentConversation from '@/api/queries/me/useGetChatAgentConversation';
import { getNewWorldTokenLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import { isDataRefreshingToolName } from '@payaca/shared-isomorphic/chatAgent/isDataRefreshingToolName';
import {
  CHAT_AGENT_EVENT_TYPES,
  type ChatAgentEvent,
  type ChatAgentEventType,
  type ChatAgentStatusCode,
} from '@payaca/types/chatAgentTypes';
import { assertUnreachable } from '@payaca/utilities/guards';

import { transformPersistedMessage } from './messageAdapter';
import type { ChatMessage, ContentBlock, ToolUse } from './types';

// Generate unique IDs - UUIDs for conversation IDs (server-validated), simple IDs for messages
const generateConversationId = () => uuid();
const generateMessageId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const API_HOST = import.meta.env.VITE_API_HOST as string;

export interface PageContext {
  pathname?: string;
  searchParams?: Record<string, string>;
  mountedQueryKeys?: unknown[][];
  /** Extra context a bound page attaches for the agent to read. */
  [key: string]: unknown;
}

/** A message the user submitted while a turn was streaming, awaiting its turn. */
export interface QueuedMessage {
  id: string;
  content: string;
  context?: PageContext;
}

interface UseChatStreamOptions {
  onNewChatConfirmation?: () => void;
  onMutationComplete?: () => void;
  onStreamComplete?: () => void;
  initialConversationId?: string;
}

interface SendMessageOptions {
  /**
   * Always start a fresh conversation, ignoring the active one. Used to launch a
   * self-contained chat (e.g. the project activity summary) without touching or
   * appending to the user's open chat.
   */
  forceNewConversation?: boolean;
}

export interface UseChatStreamReturn {
  title?: string;
  summary?: string;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  isDisabled: boolean;
  status: ChatAgentStatusCode | null;
  conversationId: string | null;
  isLoading: boolean;
  streamingConversationIds: Set<string>;
  queuedMessages: QueuedMessage[];
  sendMessage: (
    message: string,
    context?: PageContext,
    opts?: SendMessageOptions
  ) => Promise<void>;
  stopStreaming: () => void;
  interruptAndSend: (message: string, context?: PageContext) => Promise<void>;
  cancelQueuedMessage: (id: string) => void;
  clearMessages: () => void;
  switchConversation: (conversationId: string | null) => void;
}

// --- State & Actions ---

/** State for a single conversation */
interface ConversationState {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  isDisabled: boolean;
  status: ChatAgentStatusCode | null;
  /** Messages submitted during a streaming turn, awaiting auto-submission (FIFO). */
  queue: QueuedMessage[];
  /**
   * Blocks auto-submission of the queue after a turn ends in error or abort.
   * Set true on failure terminals, reset false when a fresh turn starts or
   * completes successfully. Guards against firing messages into a failing or
   * read-only conversation.
   */
  isQueuePaused: boolean;
  /**
   * The conversation began in this browser session, so no messages predate the
   * reducer and it holds the whole transcript. Callers must then not also merge
   * the persisted copy, or messages that have since been written render twice.
   */
  startedThisSession: boolean;
}

/** Multi-conversation state indexed by conversationId */
interface ChatState {
  conversations: Map<string, ConversationState>;
  activeConversationId: string | null;
}

const createEmptyConversationState = (): ConversationState => ({
  messages: [],
  isStreaming: false,
  error: null,
  isDisabled: false,
  status: null,
  queue: [],
  isQueuePaused: false,
  startedThisSession: false,
});

const initialState: ChatState = {
  conversations: new Map(),
  activeConversationId: null,
};

type ChatAction =
  | {
      type: 'SEND_MESSAGE';
      conversationId: string;
      userMessage: string;
      /** First turn of a conversation created in this session. */
      isNewConversation: boolean;
    }
  | { type: 'APPEND_TEXT'; conversationId: string; text: string }
  /** Idempotent. */
  | {
      type: 'REGISTER_TOOL';
      conversationId: string;
      toolUseId: string;
      toolName: string;
      friendlyLabel?: string;
    }
  | {
      type: 'FINALIZE_TOOLS';
      conversationId: string;
      toolUses: Array<{
        id: string;
        name: string;
        input: Record<string, unknown>;
        friendlyLabel?: string;
      }>;
    }
  | { type: 'STREAM_COMPLETE'; conversationId: string }
  /** Also removes an empty assistant message. */
  | { type: 'STREAM_ERROR'; conversationId: string; error: string }
  /**
   * `resumeQueue` leaves the queue drainable, for a deliberate stop whose
   * purpose is to move on to the messages already queued.
   */
  | { type: 'STREAM_ABORTED'; conversationId: string; resumeQueue?: boolean }
  | { type: 'RESET' }
  | {
      type: 'SWITCH_CONVERSATION';
      newConversationId: string | null;
      previousConversationId: string | null;
      clearPrevious: boolean;
    }
  | { type: 'NEW_CONVERSATION_CONFIRMED'; conversationId: string }
  /** Disables further messages. */
  | { type: 'CONTEXT_EXHAUSTED'; conversationId: string; error: string }
  | {
      type: 'SET_STATUS';
      conversationId: string;
      status: ChatAgentStatusCode | null;
    }
  | { type: 'ENQUEUE_MESSAGE'; conversationId: string; message: QueuedMessage }
  | { type: 'DEQUEUE_MESSAGE'; conversationId: string; id: string };

// --- Pure Helper Functions ---

function appendTextToBlocks(
  blocks: ContentBlock[],
  text: string
): ContentBlock[] {
  const result = [...blocks];
  const lastBlock = result[result.length - 1];

  if (lastBlock?.type === 'text') {
    result[result.length - 1] = { type: 'text', text: lastBlock.text + text };
  } else {
    result.push({ type: 'text', text });
  }

  return result;
}

function ensureToolInMessage(
  toolUses: ToolUse[],
  contentBlocks: ContentBlock[],
  toolUseId: string,
  toolName: string,
  friendlyLabel: string | undefined
): { toolUses: ToolUse[]; contentBlocks: ContentBlock[] } {
  const existing = toolUses.find((t) => t.id === toolUseId);
  if (existing) {
    // A later event can carry a richer label than the one the tool was
    // registered with (the SDK runtime only knows the tool name at start).
    if (friendlyLabel && existing.friendlyLabel !== friendlyLabel) {
      return {
        toolUses: toolUses.map((t) =>
          t.id === toolUseId ? { ...t, friendlyLabel } : t
        ),
        contentBlocks,
      };
    }
    return { toolUses, contentBlocks };
  }

  return {
    toolUses: [
      ...toolUses,
      {
        id: toolUseId,
        name: toolName,
        input: {},
        status: 'running',
        friendlyLabel,
      },
    ],
    contentBlocks: [...contentBlocks, { type: 'tool', toolId: toolUseId }],
  };
}

function completeTools(
  toolUses: ToolUse[],
  contentBlocks: ContentBlock[],
  finalTools: Array<{
    id: string;
    name: string;
    input: Record<string, unknown>;
    friendlyLabel?: string;
  }>
): { toolUses: ToolUse[]; contentBlocks: ContentBlock[] } {
  const updatedToolUses = [...toolUses];
  let updatedBlocks = contentBlocks;

  for (const newTool of finalTools) {
    const existingIndex = updatedToolUses.findIndex((t) => t.id === newTool.id);

    if (existingIndex >= 0) {
      updatedToolUses[existingIndex] = {
        ...updatedToolUses[existingIndex],
        input: newTool.input,
        status: 'complete',
        friendlyLabel:
          newTool.friendlyLabel ?? updatedToolUses[existingIndex].friendlyLabel,
      };
    } else {
      updatedToolUses.push({
        id: newTool.id,
        name: newTool.name,
        input: newTool.input,
        status: 'complete',
        friendlyLabel: newTool.friendlyLabel,
      });
      updatedBlocks = [...updatedBlocks, { type: 'tool', toolId: newTool.id }];
    }
  }

  return { toolUses: updatedToolUses, contentBlocks: updatedBlocks };
}

function updateLastAssistantMessage(
  messages: ChatMessage[],
  updater: (msg: ChatMessage) => ChatMessage
): ChatMessage[] {
  const lastMsg = messages[messages.length - 1];
  if (lastMsg?.role !== 'assistant') return messages;

  const result = [...messages];
  result[result.length - 1] = updater(lastMsg);
  return result;
}

// --- Reducer Helpers ---

/** Get conversation state, creating empty if not exists */
function getConversation(
  conversations: Map<string, ConversationState>,
  conversationId: string
): ConversationState {
  return conversations.get(conversationId) ?? createEmptyConversationState();
}

/** Update a specific conversation in the Map */
function updateConversation(
  state: ChatState,
  conversationId: string,
  updater: (conv: ConversationState) => ConversationState
): ChatState {
  const conversations = new Map(state.conversations);
  const current = getConversation(conversations, conversationId);
  conversations.set(conversationId, updater(current));
  return { ...state, conversations };
}

// --- Reducer ---

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SEND_MESSAGE': {
      const next = updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: [
          ...conv.messages,
          {
            id: generateMessageId(),
            role: 'user',
            content: action.userMessage,
            timestamp: new Date(),
          },
          {
            id: generateMessageId(),
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isStreaming: true,
            contentBlocks: [],
            toolUses: [],
          },
        ],
        isStreaming: true,
        error: null,
        status: null,
        isQueuePaused: false,
        startedThisSession: conv.startedThisSession || action.isNewConversation,
      }));
      return { ...next, activeConversationId: action.conversationId };
    }

    case 'APPEND_TEXT':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          content: msg.content + action.text,
          contentBlocks: appendTextToBlocks(
            msg.contentBlocks ?? [],
            action.text
          ),
        })),
      }));

    case 'REGISTER_TOOL':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => {
          const { toolUses, contentBlocks } = ensureToolInMessage(
            msg.toolUses ?? [],
            msg.contentBlocks ?? [],
            action.toolUseId,
            action.toolName,
            action.friendlyLabel
          );
          return { ...msg, toolUses, contentBlocks };
        }),
      }));

    case 'FINALIZE_TOOLS':
      if (!action.toolUses.length) return state;
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => {
          const { toolUses, contentBlocks } = completeTools(
            msg.toolUses ?? [],
            msg.contentBlocks ?? [],
            action.toolUses
          );
          return { ...msg, toolUses, contentBlocks };
        }),
      }));

    case 'STREAM_COMPLETE':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        isStreaming: false,
        status: null,
        isQueuePaused: false,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          isStreaming: false,
        })),
      }));

    case 'STREAM_ERROR':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(
          conv.messages.filter((m) => m.content.length > 0),
          (msg) => ({ ...msg, isStreaming: false })
        ),
        isStreaming: false,
        error: action.error,
        status: null,
        isQueuePaused: true,
      }));

    case 'STREAM_ABORTED':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          isStreaming: false,
        })),
        isStreaming: false,
        status: null,
        isQueuePaused: !action.resumeQueue,
      }));

    case 'RESET':
      return initialState;

    case 'SWITCH_CONVERSATION': {
      let conversations = state.conversations;
      if (action.clearPrevious && action.previousConversationId) {
        conversations = new Map(conversations);
        conversations.delete(action.previousConversationId);
      }
      return {
        ...state,
        conversations,
        activeConversationId: action.newConversationId,
      };
    }

    case 'NEW_CONVERSATION_CONFIRMED':
      return {
        ...state,
        activeConversationId: action.conversationId,
      };

    case 'CONTEXT_EXHAUSTED':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(
          conv.messages.filter((m) => m.content.length > 0),
          (msg) => ({ ...msg, isStreaming: false })
        ),
        isStreaming: false,
        error: action.error,
        isDisabled: true,
        status: null,
        isQueuePaused: true,
      }));

    case 'SET_STATUS':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        status: action.status,
      }));

    case 'ENQUEUE_MESSAGE':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        queue: [...conv.queue, action.message],
      }));

    case 'DEQUEUE_MESSAGE':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        queue: conv.queue.filter((m) => m.id !== action.id),
      }));

    default:
      return state;
  }
}

// --- Server-Sent Events Stream Parser ---

async function* parseServerSentEventsStream(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<ChatAgentEvent> {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEventType = line.slice(7).trim();
        continue;
      }

      if (line.startsWith('data: ')) {
        try {
          const data: unknown = JSON.parse(line.slice(6));
          if (currentEventType && isValidEventType(currentEventType)) {
            yield { type: currentEventType, data } as ChatAgentEvent;
          }
        } catch {
          // Ignore malformed JSON
        }
      }
    }
  }
}

function isValidEventType(type: string): type is ChatAgentEventType {
  return CHAT_AGENT_EVENT_TYPES.includes(type as ChatAgentEventType);
}

// --- Main Hook ---

export const useChatStream = (
  options?: UseChatStreamOptions
): UseChatStreamReturn => {
  const queryClient = useQueryClient();

  // Initialize state with initial conversation ID if provided
  const [state, dispatch] = useReducer(chatReducer, {
    conversations: new Map<string, ConversationState>(),
    activeConversationId: options?.initialConversationId ?? null,
  });

  const abortControllersRef = useRef(new Map<string, AbortController>());

  const activeConversationId = state.activeConversationId;

  const activeConversation = activeConversationId
    ? state.conversations.get(activeConversationId)
    : undefined;

  // Skipped while the conversation has messages in the reducer: they are the
  // live session copy, so fetching would flash a skeleton and risk duplicates.
  const { data: conversationData, isLoading } = useGetChatAgentConversation(
    activeConversationId,
    {
      enabled: !!activeConversationId && !activeConversation?.messages.length,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const messages = useMemo((): ChatMessage[] => {
    const sessionMessages = activeConversation?.messages ?? [];

    // The reducer is the complete transcript for a conversation started this
    // session, so skip persisted to avoid rendering messages twice.
    if (activeConversation?.startedThisSession && sessionMessages.length > 0) {
      return sessionMessages;
    }

    const persisted = conversationData?.chatAgentConversation?.messages ?? [];
    return [...persisted.map(transformPersistedMessage), ...sessionMessages];
  }, [
    conversationData,
    activeConversation?.messages,
    activeConversation?.startedThisSession,
  ]);

  const streamingConversationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [id, conv] of state.conversations) {
      if (conv.isStreaming) {
        ids.add(id);
      }
    }
    return ids;
  }, [state.conversations]);

  // Callers guarantee the conversation is not already streaming.
  const runStream = useCallback(
    async (
      conversationId: string,
      message: string,
      context: PageContext | undefined,
      isNewConversation: boolean
    ) => {
      abortControllersRef.current.get(conversationId)?.abort();
      const abortController = new AbortController();
      abortControllersRef.current.set(conversationId, abortController);

      dispatch({
        type: 'SEND_MESSAGE',
        conversationId,
        userMessage: message,
        isNewConversation,
      });

      const MAX_RETRIES = 3;

      // The conversation id is its checkpointer thread, so a retry after the
      // `conversation` event must resume rather than ask for a second one.
      let createAsNewConversation = isNewConversation;

      // Spans retries, since a retried turn may have already written before
      // failing.
      let changedServerData = false;

      type StreamResult =
        | { outcome: 'success' }
        | { outcome: 'abort' }
        | { outcome: 'terminal_error'; message: string }
        | { outcome: 'retriable_error' };

      const attemptStream = async (): Promise<StreamResult> => {
        const token =
          localStorage.getItem(getNewWorldTokenLocalStorageKey()) ||
          localStorage.getItem(TOKEN_KEY);

        const response = await fetch(
          `${API_HOST}/provider/rest/chat-agent/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              message,
              context,
              // For existing conversations, send the ID
              // For new conversations, send as newConversationId so server creates with this ID
              conversationId: createAsNewConversation
                ? undefined
                : conversationId,
              newConversationId: createAsNewConversation
                ? conversationId
                : undefined,
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        let receivedContent = false;
        let errorMessage: string | null = null;

        for await (const event of parseServerSentEventsStream(reader)) {
          switch (event.type) {
            case 'text':
              receivedContent = true;
              dispatch({
                type: 'APPEND_TEXT',
                conversationId,
                text: event.data.text,
              });
              break;

            case 'tool_start':
            case 'tool_progress':
              receivedContent = true;
              dispatch({
                type: 'REGISTER_TOOL',
                conversationId,
                toolUseId: event.data.toolUseId,
                toolName: event.data.toolName,
                // tool_progress shares this branch and carries no label.
                friendlyLabel:
                  'friendlyLabel' in event.data
                    ? event.data.friendlyLabel
                    : undefined,
              });
              break;

            case 'message':
              if (event.data.toolUses?.length) {
                receivedContent = true;
                dispatch({
                  type: 'FINALIZE_TOOLS',
                  conversationId,
                  toolUses: event.data.toolUses,
                });

                // This event precedes the tool actually running, so the refresh
                // may read the pre-change value; the `finally` refreshes again.
                const changedData = event.data.toolUses.some((tool) =>
                  isDataRefreshingToolName(tool.name)
                );
                if (changedData) {
                  changedServerData = true;
                  options?.onMutationComplete?.();
                }
              }
              break;

            case 'error':
              if (event.data.code === 'CONTEXT_EXHAUSTED') {
                dispatch({
                  type: 'CONTEXT_EXHAUSTED',
                  conversationId,
                  error: event.data.message,
                });
                return { outcome: 'abort' };
              }
              // Explicit error from server — don't retry
              errorMessage = event.data.message;
              break;

            case 'conversation':
              // Server confirms the conversation ID
              if (createAsNewConversation) {
                createAsNewConversation = false;
                dispatch({
                  type: 'NEW_CONVERSATION_CONFIRMED',
                  conversationId: event.data.conversationId,
                });
                options?.onNewChatConfirmation?.();
              }
              break;

            case 'status':
              dispatch({
                type: 'SET_STATUS',
                conversationId,
                status: event.data.status,
              });
              break;

            case 'result':
              if (event.data.isError && !errorMessage) {
                errorMessage = 'Something went wrong. Please try again.';
              }
              break;

            case 'done':
              // Informational event - no action needed
              break;
          }
        }

        if (errorMessage) {
          return { outcome: 'terminal_error', message: errorMessage };
        }
        if (!receivedContent) {
          return { outcome: 'retriable_error' };
        }
        return { outcome: 'success' };
      };

      try {
        let lastError: string | undefined;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, 1000 * attempt));
            dispatch({
              type: 'SET_STATUS',
              conversationId,
              status: 'retrying',
            });
          }

          try {
            const result = await attemptStream();
            switch (result.outcome) {
              case 'success':
                dispatch({ type: 'STREAM_COMPLETE', conversationId });
                options?.onStreamComplete?.();
                return;
              case 'abort':
                // CONTEXT_EXHAUSTED was already dispatched
                return;
              case 'terminal_error':
                // Explicit error from server — don't retry
                dispatch({
                  type: 'STREAM_ERROR',
                  conversationId,
                  error: result.message,
                });
                return;
              case 'retriable_error':
                // Empty response — try again
                lastError = 'Something went wrong. Please try again.';
                break;
              default:
                assertUnreachable(result);
            }
          } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
              // Only report the abort if this turn still owns the conversation's
              // controller. An interrupt-and-send aborts this turn and starts a
              // fresh one, replacing the controller; the stale handler must not
              // clobber the new turn's streaming state.
              if (
                abortControllersRef.current.get(conversationId) ===
                abortController
              ) {
                dispatch({ type: 'STREAM_ABORTED', conversationId });
              }
              return;
            }
            lastError =
              err instanceof Error ? err.message : 'An error occurred';
          }
        }

        // All retries exhausted
        dispatch({
          type: 'STREAM_ERROR',
          conversationId,
          error: lastError ?? 'An error occurred',
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          if (
            abortControllersRef.current.get(conversationId) === abortController
          ) {
            dispatch({ type: 'STREAM_ABORTED', conversationId });
          }
          return;
        }
        dispatch({
          type: 'STREAM_ERROR',
          conversationId,
          error: err instanceof Error ? err.message : 'An error occurred',
        });
      } finally {
        // The stream has drained and every tool has finished, so refresh again
        // in case the mid-stream refresh read a pre-change value.
        if (changedServerData) {
          options?.onMutationComplete?.();
        }
      }
    },
    [options]
  );

  const sendMessage = useCallback(
    async (
      message: string,
      context?: PageContext,
      opts?: SendMessageOptions
    ) => {
      // `forceNewConversation` ignores the active conversation.
      let conversationId = opts?.forceNewConversation
        ? null
        : activeConversationId;
      const isNewConversation = !conversationId;

      if (!conversationId) {
        conversationId = generateConversationId();
      }

      // If the target conversation is mid-turn, enqueue for auto-submission
      // rather than sending immediately.
      const existingConv = state.conversations.get(conversationId);
      if (existingConv?.isStreaming) {
        dispatch({
          type: 'ENQUEUE_MESSAGE',
          conversationId,
          message: { id: generateMessageId(), content: message, context },
        });
        return;
      }

      await runStream(conversationId, message, context, isNewConversation);
    },
    [activeConversationId, state.conversations, runStream]
  );

  // Interrupt the active conversation's in-flight turn, preserving the partial
  // assistant message already streamed.
  //
  // A stop with messages already queued means the user wants to move on to
  // those, so the queue is left drainable and the auto-submit effect sends the
  // next one straight away; the rest follow as each turn completes. A stop with
  // an empty queue pauses as any other abort does.
  //
  // The controller is dropped before aborting so the interrupted turn's own
  // AbortError handler sees it was superseded and skips its dispatch — leaving
  // this one authoritative, rather than racing to re-pause the queue behind us.
  const stopStreaming = useCallback(() => {
    if (!activeConversationId) return;
    const controller = abortControllersRef.current.get(activeConversationId);
    if (!controller) return;
    abortControllersRef.current.delete(activeConversationId);
    controller.abort();
    const hasQueued =
      (state.conversations.get(activeConversationId)?.queue.length ?? 0) > 0;
    dispatch({
      type: 'STREAM_ABORTED',
      conversationId: activeConversationId,
      resumeQueue: hasQueued,
    });
  }, [activeConversationId, state.conversations]);

  // Distinct from queueing, which waits for the current turn to finish.
  const interruptAndSend = useCallback(
    async (message: string, context?: PageContext) => {
      if (!activeConversationId) {
        await sendMessage(message, context);
        return;
      }
      abortControllersRef.current.get(activeConversationId)?.abort();
      // The aborted turn's own AbortError handler skips this once runStream
      // replaces the controller below, so the old message would otherwise keep
      // its streaming indicator.
      dispatch({
        type: 'STREAM_ABORTED',
        conversationId: activeConversationId,
      });
      await runStream(activeConversationId, message, context, false);
    },
    [activeConversationId, runStream, sendMessage]
  );

  // runStream dispatches SEND_MESSAGE synchronously, flipping isStreaming true
  // in the same render, so a single message never drains twice.
  useEffect(() => {
    if (!activeConversationId) return;
    const conv = state.conversations.get(activeConversationId);
    if (!conv) return;
    if (
      conv.isStreaming ||
      conv.isDisabled ||
      conv.isQueuePaused ||
      conv.queue.length === 0
    ) {
      return;
    }

    const next = conv.queue[0];
    dispatch({
      type: 'DEQUEUE_MESSAGE',
      conversationId: activeConversationId,
      id: next.id,
    });
    void runStream(activeConversationId, next.content, next.context, false);
  }, [activeConversationId, state.conversations, runStream]);

  const cancelQueuedMessage = useCallback(
    (id: string) => {
      if (!activeConversationId) return;
      dispatch({
        type: 'DEQUEUE_MESSAGE',
        conversationId: activeConversationId,
        id,
      });
    },
    [activeConversationId]
  );

  const switchConversation = useCallback(
    (newId: string | null) => {
      const shouldClearPrevious =
        activeConversationId &&
        activeConversation &&
        !activeConversation.isStreaming;

      dispatch({
        type: 'SWITCH_CONVERSATION',
        newConversationId: newId,
        previousConversationId: activeConversationId,
        clearPrevious: !!shouldClearPrevious,
      });

      if (shouldClearPrevious) {
        void queryClient.invalidateQueries({
          queryKey: meKeys.chatAgentConversation(activeConversationId),
        });
      }
    },
    [activeConversationId, activeConversation, queryClient]
  );

  const clearMessages = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    title: conversationData?.chatAgentConversation?.title || undefined,
    summary: conversationData?.chatAgentConversation?.summary || undefined,
    messages,
    isStreaming: activeConversation?.isStreaming ?? false,
    error: activeConversation?.error ?? null,
    isDisabled: activeConversation?.isDisabled ?? false,
    status: activeConversation?.status ?? null,
    conversationId: activeConversationId,
    isLoading,
    streamingConversationIds,
    queuedMessages: activeConversation?.queue ?? [],
    sendMessage,
    stopStreaming,
    interruptAndSend,
    cancelQueuedMessage,
    clearMessages,
    switchConversation,
  };
};
